import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { ImageOffIcon, Loader2Icon, ShrinkIcon, XIcon, ZoomInIcon, ZoomOutIcon } from "lucide-react"
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react"
import { Button } from "@/primitives/button.tsx"
import { cn } from "@/primitives/utils"
import { useFullScreenTask } from "./full-screen-task.ts"
import {
  fitted,
  pannedBy,
  shownWords,
  stepFactor,
  unzoomed,
  viewerKeyAsked,
  wheelFactor,
  zoomedAt,
  type ImageView,
  type Size,
} from "./image-viewer.ts"
import { useBackDismiss } from "./use-back-dismiss.ts"
import { useViewport } from "./viewport.tsx"

/*
 * One image at full size, to read its detail: a Document's mock-up enlarged from its preview. It
 * fills the screen on the near-black `lightbox`, on the desk and on a phone alike, its alt text
 * naming it over the image (and to a screen reader), with Zoom out, how large it shows, Zoom in,
 * Fit and Close along the top.
 *
 * The image opens fitted to the stage, never past its own size, and zooms up to eight times that
 * (`image-viewer.ts`): by the buttons, by +, - and 0 (with Ctrl or ⌘ too, which the browser would
 * otherwise take), by the wheel or a trackpad's pinch where the pointer is, by a pinch on a touch
 * screen between the fingers, and by a double press where it lands, which zooms back out once
 * zoomed. Zoomed, it pans by a drag or the arrow keys, only as far as keeps it over the stage.
 * Escape closes it, as does a press on the stage outside the image, and on a phone Back. The image
 * loads only once it is opened, so a Document with many images fetches none of them full size
 * until one is asked for; one that can't be loaded says so.
 */

/** The image to show: where it loads from, and its alt text, which names it. */
export type ViewedImage = { src: string; alt: string }

/** A control on the lightbox: light on the near-black, its press a light wash. */
const onLightbox =
  "text-lightbox-foreground hover:bg-lightbox-foreground/10 hover:text-lightbox-foreground focus-visible:ring-offset-lightbox"

/** How far a press may move, in px, and still be a press rather than a drag. */
const slop = 4

/** `image`, while it is open, or none; `onClose` hears every way it closes. */
export function ImageViewer({
  image,
  onClose,
}: {
  image: ViewedImage | null
  onClose: () => void
}) {
  const open = image !== null
  const { compact } = useViewport()
  useBackDismiss(open && compact, onClose)
  useFullScreenTask(open && compact)
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-50 bg-lightbox data-open:animate-in data-open:fade-in-0 data-open:duration-200 data-closed:animate-out data-closed:fade-out-0 data-closed:duration-150" />
        {image && <Viewer key={image.src} image={image} onClose={onClose} />}
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/** A finger or the mouse down on the stage: where it is now, and whether it went down on the image. */
type Pointer = { x: number; y: number; onImage: boolean; moved: boolean }

function Viewer({ image, onClose }: { image: ViewedImage; onClose: () => void }) {
  const stage = useRef<HTMLDivElement>(null)
  const [stageSize, setStageSize] = useState<Size>({ width: 0, height: 0 })
  const [natural, setNatural] = useState<Size | null>(null)
  const [failed, setFailed] = useState(false)
  const [view, setView] = useState<ImageView>(unzoomed)
  const shown = natural ? fitted(natural, stageSize) : { width: 0, height: 0 }
  // The newest sizes, for what the wheel and the fingers do between renders.
  const sizes = useRef({ shown, stageSize })
  sizes.current = { shown, stageSize }

  // The stage's size, as it opens and as the window changes.
  useLayoutEffect(() => {
    const measure = () => {
      const box = stage.current?.getBoundingClientRect()
      if (box) setStageSize({ width: box.width, height: box.height })
    }
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [])

  /** Where `event` is, from the stage's middle. */
  const fromMiddle = (event: { clientX: number; clientY: number }) => {
    const box = stage.current?.getBoundingClientRect()
    if (!box) return { x: 0, y: 0 }
    return {
      x: event.clientX - box.left - box.width / 2,
      y: event.clientY - box.top - box.height / 2,
    }
  }
  const zoomBy = (factor: number, at = { x: 0, y: 0 }) =>
    setView((now) => zoomedAt(now, factor, at, sizes.current.shown, sizes.current.stageSize))
  const panBy = (dx: number, dy: number) =>
    setView((now) => pannedBy(now, dx, dy, sizes.current.shown, sizes.current.stageSize))

  // The wheel zooms where the pointer is. React's wheel listener is passive, and this one must keep
  // the page, and the browser's own zoom on a trackpad's pinch, from taking the turn.
  useEffect(() => {
    const element = stage.current
    if (!element) return
    const wheeled = (event: WheelEvent) => {
      event.preventDefault()
      const at = fromMiddle(event)
      setView((now) =>
        zoomedAt(
          now,
          wheelFactor(event.deltaY, event.deltaMode),
          at,
          sizes.current.shown,
          sizes.current.stageSize,
        ),
      )
    }
    element.addEventListener("wheel", wheeled, { passive: false })
    return () => element.removeEventListener("wheel", wheeled)
  }, [])

  // Each finger (or the mouse) down on the stage: one drags, two pinch.
  const pointers = useRef(new Map<number, Pointer>())
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      // A pointer the browser no longer has is followed without it.
    }
    pointers.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      onImage: event.target instanceof HTMLImageElement,
      moved: false,
    })
  }
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const pointer = pointers.current.get(event.pointerId)
    if (!pointer) return
    const others = [...pointers.current].filter(([id]) => id !== event.pointerId)
    const [other] = others
    const dx = event.clientX - pointer.x
    const dy = event.clientY - pointer.y
    const moved = pointer.moved || Math.hypot(dx, dy) > slop
    pointers.current.set(event.pointerId, { ...pointer, x: event.clientX, y: event.clientY, moved })
    if (!other) return panBy(dx, dy)
    // A pinch: the image zooms by how far the fingers spread, about the point between them, and
    // moves as that point does.
    const partner = other[1]
    const before = Math.hypot(pointer.x - partner.x, pointer.y - partner.y)
    const after = Math.hypot(event.clientX - partner.x, event.clientY - partner.y)
    if (before <= 0) return
    const between = fromMiddle({
      clientX: (event.clientX + partner.x) / 2,
      clientY: (event.clientY + partner.y) / 2,
    })
    zoomBy(after / before, between)
    panBy(dx / 2, dy / 2)
  }
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const pointer = pointers.current.get(event.pointerId)
    pointers.current.delete(event.pointerId)
    // A press outside the image, neither a drag nor a pinch, closes it.
    if (pointer && !pointer.moved && !pointer.onImage && pointers.current.size === 0) onClose()
  }
  const onPointerCancel = (event: PointerEvent<HTMLDivElement>) =>
    pointers.current.delete(event.pointerId)

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const asked = viewerKeyAsked(event)
    if (!asked) return
    event.preventDefault()
    if (asked.kind === "pan") return panBy(asked.dx, asked.dy)
    if (asked.asked === "fit") return setView(unzoomed)
    zoomBy(asked.asked === "in" ? stepFactor : 1 / stepFactor)
  }

  const name = image.alt.trim() || "Image"
  const loaded = natural !== null && !failed
  return (
    <DialogPrimitive.Popup
      data-image-viewer=""
      initialFocus={stage}
      onKeyDown={onKeyDown}
      className="fixed inset-0 z-50 flex flex-col pt-safe pb-safe text-lightbox-foreground outline-none data-open:animate-in data-open:fade-in-0 data-open:duration-200 data-closed:animate-out data-closed:fade-out-0 data-closed:duration-150"
    >
      <div className="flex h-14 shrink-0 items-center gap-1 px-2">
        <DialogPrimitive.Title className="min-w-0 flex-1 truncate px-2 text-ui font-medium">
          {name}
        </DialogPrimitive.Title>
        <div role="group" aria-label="Zoom" className="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Zoom out"
            disabled={!loaded || view.scale <= 1}
            onClick={() => zoomBy(1 / stepFactor)}
            className={onLightbox}
          >
            <ZoomOutIcon />
          </Button>
          <span
            aria-live="polite"
            className="w-12 text-center text-xs text-lightbox-foreground/70 tabular-nums"
          >
            {loaded && natural ? shownWords(view, shown, natural) : ""}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Zoom in"
            disabled={!loaded}
            onClick={() => zoomBy(stepFactor)}
            className={onLightbox}
          >
            <ZoomInIcon />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Fit to screen"
            disabled={!loaded || view.scale <= 1}
            onClick={() => setView(unzoomed)}
            className={onLightbox}
          >
            <ShrinkIcon />
          </Button>
        </div>
        <DialogPrimitive.Close
          render={<Button variant="ghost" size="icon" className={onLightbox} />}
        >
          <XIcon />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </div>
      <div
        ref={stage}
        tabIndex={-1}
        data-stage=""
        className={cn(
          "relative min-h-0 flex-1 touch-none overflow-hidden outline-none select-none",
          view.scale > 1 && "cursor-grab active:cursor-grabbing",
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        {failed ? (
          <p className="absolute inset-0 flex items-center justify-center gap-2 text-ui text-lightbox-foreground/70">
            <ImageOffIcon aria-hidden className="size-4" />
            This image couldn't be loaded
          </p>
        ) : (
          !natural && (
            <p className="absolute inset-0 flex items-center justify-center gap-2 text-ui text-lightbox-foreground/70">
              <Loader2Icon aria-hidden className="size-4 animate-spin" />
              Loading image…
            </p>
          )
        )}
        {!failed && (
          <img
            src={image.src}
            alt={image.alt}
            draggable={false}
            onLoad={(event) =>
              setNatural({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }
            onError={() => setFailed(true)}
            onDoubleClick={(event) =>
              view.scale > 1
                ? setView(unzoomed)
                : zoomBy(stepFactor * stepFactor, fromMiddle(event))
            }
            className={cn(
              "absolute top-1/2 left-1/2 max-w-none",
              natural ? (view.scale > 1 ? "cursor-grab" : "cursor-zoom-in") : "invisible",
            )}
            style={
              natural
                ? {
                    width: shown.width,
                    height: shown.height,
                    transform: `translate(-50%, -50%) translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
                  }
                : undefined
            }
          />
        )}
      </div>
    </DialogPrimitive.Popup>
  )
}
